import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { formatLocalDateYMD, parseDateOnlyToLocal } from '@shared/lib/formatLocalDate';
import { useCallback, useEffect, useState } from 'react';

/** Estado local de filtros para facturas (lista) */
export interface InvoicesFiltersState {
  erpNumber: string;
  dateRange: { from: Date | undefined; to: Date | undefined };
  grandTotalMin: string;
  grandTotalMax: string;
}

const defaultState: InvoicesFiltersState = {
  erpNumber: '',
  dateRange: { from: undefined, to: undefined },
  grandTotalMin: '',
  grandTotalMax: '',
};

function cloneDefaultState(): InvoicesFiltersState {
  return {
    ...defaultState,
    dateRange: { ...defaultState.dateRange },
  };
}

function parseConditions(conditions: FilterConditionOutput[]): InvoicesFiltersState {
  const state = cloneDefaultState();

  for (const c of conditions) {
    if (c.column === 'erp_number' && c.value != null && c.value !== '') {
      const val = c.value;
      state.erpNumber = Array.isArray(val) ? String(val[0] ?? '') : String(val);
    } else if (c.column === 'date') {
      if (c.action === 'greater_equal' && c.value) {
        state.dateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.dateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'grand_total') {
      if (c.action === 'greater_equal' || c.action === 'greater_than') {
        state.grandTotalMin = c.value != null ? String(c.value) : '';
      } else if (c.action === 'less_equal' || c.action === 'less_than') {
        state.grandTotalMax = c.value != null ? String(c.value) : '';
      }
    }
  }

  return state;
}

function convertToConditions(state: InvoicesFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.erpNumber.trim()) {
    conditions.push({
      column: 'erp_number',
      action: 'in',
      value: [state.erpNumber.trim()],
    });
  }
  if (state.dateRange.from) {
    conditions.push({
      column: 'date',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.dateRange.from),
    });
  }
  if (state.dateRange.to) {
    conditions.push({
      column: 'date',
      action: 'less_equal',
      value: formatLocalDateYMD(state.dateRange.to),
    });
  }
  if (state.grandTotalMin.trim()) {
    const num = parseFloat(state.grandTotalMin);
    if (!Number.isNaN(num)) {
      conditions.push({
        column: 'grand_total',
        action: 'greater_equal',
        value: num,
      });
    }
  }
  if (state.grandTotalMax.trim()) {
    const num = parseFloat(state.grandTotalMax);
    if (!Number.isNaN(num)) {
      conditions.push({
        column: 'grand_total',
        action: 'less_equal',
        value: num,
      });
    }
  }

  return conditions;
}

export interface UseInvoicesFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

export const useInvoicesFilters = ({ initialConditions = [], onChange }: UseInvoicesFiltersParams) => {
  const [filters, setFilters] = useState<InvoicesFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: InvoicesFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleErpNumberChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, erpNumber: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, dateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleGrandTotalMinChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, grandTotalMin: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleGrandTotalMaxChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, grandTotalMax: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    const empty = cloneDefaultState();
    setFilters(empty);
    notifyChange(empty);
  }, [notifyChange]);

  return {
    filters,
    handleErpNumberChange,
    handleDateRangeChange,
    handleGrandTotalMinChange,
    handleGrandTotalMaxChange,
    clearAllFilters,
  };
};
